import { useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import {
  ExternalLink,
  FileText,
  LoaderCircle,
  Paperclip,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import {
  api,
  uploadClinicalFile,
  type ClinicalAttachment,
  type ClinicalUploadIntent,
  type CrmVaccination,
  type VaccinationInput,
} from "./api";

const ACCEPTED_DOCUMENT_TYPES = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;

interface DocumentsProps {
  organizationId: string;
  petId: string;
  consultationId: string;
  initialCount: number;
  canAttach: boolean;
  onCountChange: (count: number) => void;
  transport?: {
    list: () => Promise<ClinicalAttachment[]>;
    intent: (
      file: File,
      metadata: { category: string; title: string; documentDate: string },
    ) => Promise<ClinicalUploadIntent>;
    complete: (assetId: string) => Promise<unknown>;
  };
}

export function ClinicalDocuments({
  organizationId,
  petId,
  consultationId,
  initialCount,
  canAttach,
  onCountChange,
  transport,
}: DocumentsProps): ReactNode {
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [attachments, setAttachments] = useState<ClinicalAttachment[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documentCategory, setDocumentCategory] = useState("OTHER");
  const [error, setError] = useState<string | null>(null);

  async function load(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const result = transport
        ? await transport.list()
        : await api.clinicalAttachments(organizationId, petId, consultationId);
      setAttachments(result);
      onCountChange(result.length);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos abrir los documentos.");
    } finally {
      setLoading(false);
    }
  }

  async function toggle(): Promise<void> {
    const next = !open;
    setOpen(next);
    if (next && attachments === null) await load();
  }

  async function upload(file: File): Promise<void> {
    setError(null);
    if (!ACCEPTED_DOCUMENT_TYPES.includes(file.type)) {
      setError("Elegí una imagen JPG, PNG, WEBP o un PDF.");
      return;
    }
    if (file.size <= 0 || file.size > MAX_DOCUMENT_BYTES) {
      setError("El documento debe pesar hasta 20 MB.");
      return;
    }
    setUploading(true);
    try {
      const metadata = {
        category: documentCategory,
        title: file.name,
        documentDate: new Date().toISOString(),
      };
      const intent = transport
        ? await transport.intent(file, metadata)
        : await api.createClinicalAttachmentIntent(
            organizationId,
            petId,
            consultationId,
            file,
            metadata,
          );
      await uploadClinicalFile(intent, file);
      if (transport) await transport.complete(intent.assetId);
      else
        await api.completeClinicalAttachment(organizationId, petId, consultationId, intent.assetId);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos guardar el documento.");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  return (
    <div className="clinical-documents">
      <div className="clinical-document-actions">
        <button
          className="button-link"
          type="button"
          onClick={() => void toggle()}
          aria-expanded={open}
        >
          <Paperclip size={14} aria-hidden="true" />
          Documentos {initialCount > 0 ? `(${initialCount})` : ""}
        </button>
        {canAttach && (
          <>
            <label className="visually-hidden" htmlFor={`document-category-${consultationId}`}>
              Tipo de documento
            </label>
            <select
              id={`document-category-${consultationId}`}
              className="clinical-document-category"
              value={documentCategory}
              onChange={(event) => setDocumentCategory(event.target.value)}
              disabled={uploading}
            >
              <option value="OTHER">Otro</option>
              <option value="LABORATORY">Laboratorio</option>
              <option value="IMAGING">Imágenes</option>
              <option value="PRESCRIPTION">Receta</option>
              <option value="CERTIFICATE">Certificado</option>
              <option value="SURGERY">Cirugía</option>
              <option value="DERIVATION">Derivación</option>
              <option value="CONSENT">Consentimiento</option>
              <option value="REPORT">Informe</option>
            </select>
            <input
              ref={inputRef}
              className="visually-hidden"
              type="file"
              tabIndex={-1}
              accept={ACCEPTED_DOCUMENT_TYPES.join(",")}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  setOpen(true);
                  void upload(file);
                }
              }}
            />
            <button
              className="button-link button-link-success"
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? (
                <LoaderCircle className="spin" size={14} aria-hidden="true" />
              ) : (
                <Upload size={14} aria-hidden="true" />
              )}
              {uploading ? "Guardando…" : "Adjuntar"}
            </button>
          </>
        )}
      </div>
      {open && (
        <div className="clinical-document-tray">
          <div className="clinical-document-tray-heading">
            <span>
              <ShieldCheck size={14} aria-hidden="true" /> Custodia privada
            </span>
            <button
              className="icon-button"
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Cerrar documentos"
            >
              <X size={15} />
            </button>
          </div>
          {loading ? (
            <p className="document-state" role="status">
              <LoaderCircle className="spin" size={15} aria-hidden="true" />
              Abriendo documentos…
            </p>
          ) : error ? (
            <div className="document-error" role="alert">
              <p>{error}</p>
              <button className="button-link" type="button" onClick={() => void load()}>
                Reintentar
              </button>
            </div>
          ) : attachments?.length ? (
            <ul className="clinical-document-list">
              {attachments.map((attachment) => (
                <li key={attachment.id}>
                  <FileText size={17} aria-hidden="true" />
                  <span>
                    <strong>{attachment.title}</strong>
                    <small>
                      {attachment.category} ·{" "}
                      {attachment.sourceType === "TUTOR_REPORTED"
                        ? "aportado por tutor"
                        : attachment.authorName
                          ? `registrado por ${attachment.authorName}`
                          : "documento clínico"}
                      <br />
                      {formatBytes(attachment.sizeBytes)}
                      {attachment.uploadedAt
                        ? ` · ${new Intl.DateTimeFormat("es-AR", {
                            dateStyle: "medium",
                          }).format(new Date(attachment.uploadedAt))}`
                        : ""}
                    </small>
                  </span>
                  <a
                    className="icon-button"
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={`Abrir ${attachment.title}`}
                  >
                    <ExternalLink size={15} />
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="document-state">Todavía no hay documentos en esta atención.</p>
          )}
        </div>
      )}
    </div>
  );
}

interface VaccinationProps {
  patientName: string;
  providerName: string;
  onCancel: () => void;
  onCreated: (input: VaccinationInput, idempotencyKey: string) => Promise<CrmVaccination>;
}

export function VaccinationComposer({
  patientName,
  providerName,
  onCancel,
  onCreated,
}: VaccinationProps): ReactNode {
  const [name, setName] = useState("");
  const [administeredAt, setAdministeredAt] = useState(todayValue);
  const [nextDueAt, setNextDueAt] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    if (nextDueAt && nextDueAt < administeredAt) {
      setError("El próximo vencimiento no puede ser anterior a la aplicación.");
      return;
    }
    setSubmitting(true);
    const input: VaccinationInput = {
      name: name.trim(),
      administeredAt: new Date(`${administeredAt}T00:00:00`).toISOString(),
      providerName,
      ...(nextDueAt ? { nextDueAt: new Date(`${nextDueAt}T00:00:00`).toISOString() } : {}),
      ...(batchNumber.trim() ? { batchNumber: batchNumber.trim() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };
    try {
      await onCreated(input, idempotencyKey);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos registrar la vacuna.");
      setSubmitting(false);
    }
  }

  return (
    <form
      className="vaccination-composer"
      onSubmit={(event) => void submit(event)}
      aria-labelledby="vaccination-composer-title"
    >
      <div className="clinical-document-tray-heading">
        <div>
          <span className="eyebrow">Prevención verificable</span>
          <h3 id="vaccination-composer-title">Registrar vacuna de {patientName}</h3>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onCancel}
          disabled={submitting}
          aria-label="Cerrar registro de vacuna"
        >
          <X size={16} />
        </button>
      </div>
      <label>
        <span>Vacuna</span>
        <input
          value={name}
          onChange={(event) => setName(event.target.value)}
          minLength={2}
          maxLength={160}
          autoFocus
          required
        />
      </label>
      <div className="vaccination-date-grid">
        <label>
          <span>Fecha de aplicación</span>
          <input
            type="date"
            value={administeredAt}
            max={todayValue()}
            onChange={(event) => setAdministeredAt(event.target.value)}
            required
          />
        </label>
        <label>
          <span>Próximo vencimiento</span>
          <input
            type="date"
            value={nextDueAt}
            min={administeredAt}
            onChange={(event) => setNextDueAt(event.target.value)}
          />
        </label>
      </div>
      <label>
        <span>Lote</span>
        <input
          value={batchNumber}
          onChange={(event) => setBatchNumber(event.target.value)}
          maxLength={80}
          placeholder="Opcional"
        />
      </label>
      <label>
        <span>Notas</span>
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          maxLength={500}
          rows={3}
          placeholder="Observaciones o indicaciones preventivas"
        />
      </label>
      {nextDueAt && (
        <p className="preventive-reminder-note">
          Tappet avisará al tutor cuando el refuerzo entre en la ventana de 30 días.
        </p>
      )}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button
          className="button button-secondary button-compact"
          type="button"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancelar
        </button>
        <button
          className="button button-primary button-compact"
          type="submit"
          disabled={submitting}
        >
          {submitting && <LoaderCircle className="spin" size={15} aria-hidden="true" />}
          {submitting ? "Registrando…" : "Registrar vacuna"}
        </button>
      </div>
    </form>
  );
}

export { MedicationPanel } from "./medication-panel";

function todayValue(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
